<template>
  <div class="part-mobile">
    <!-- Filter summary bar -->
    <part-filter-row 
      @click="filterVisible = true"
      :filter="filter"
      :count="rows.length"
      :total="total"
    ></part-filter-row>

    <!-- Action buttons -->
    <div class="container-fluid px-0 my-2">
      <div class="row g-2">
        <div class="col-4">
          <button @click="refresh()"
            class="btn btn-primary form-control"
          >
            <i class="fa fa-sync"></i> Refresh
          </button>
        </div>
        <div class="col-4">
          <button @click="newRecord()"
            class="btn btn-success form-control"
          >
            <i class="fa fa-plus"></i> New
          </button>
        </div>
        <div class="col-4">
          <button @click="filterVisible = true"
            class="btn btn-outline-secondary form-control"
          >
            <i class="fa fa-filter"></i> Filter
          </button>
        </div>
      </div>
    </div>

    <hr class="my-2" />

    <!-- Mobile-optimized list view -->
    <div class="mobile-part-list">
      <div v-for="row in rows" :key="row.Id" class="part-card mb-2" @click="editRecord(row.Id)">
        <div class="card">
          <div class="card-body p-2">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div class="flex-grow-1">
                <strong>{{ row.Description || 'No Description' }}</strong>
                <div class="text-muted small">{{ row.Contact?.Company || 'N/A' }}</div>
              </div>
              <div class="text-end ms-2">
                <span v-if="row.OrderStatus" :class="getOrderStatusBadgeClass(row.OrderStatus)" class="badge">
                  {{ getOrderStatusName(row.OrderStatus) }}
                </span>
              </div>
            </div>
            
            <div class="row small mb-1">
              <div class="col-6">
                <i class="fas fa-box me-1"></i>Qty: {{ row.Quantity || 0 }}
              </div>
              <div class="col-6 text-end">
                <i class="fas fa-dollar-sign me-1"></i>{{ (row.Cost || 0).toFixed(2) }}
              </div>
            </div>
            
            <div class="small mb-1" v-if="row.Request?.Id">
              <i class="fas fa-ticket-alt me-1"></i>Request #{{ row.Request.Id }}
            </div>
            
            <div class="small mb-1" v-if="row.Technician?.TechnicianName">
              <i class="fas fa-user me-1"></i>{{ row.Technician.TechnicianName }}
            </div>
            
            <div class="small text-truncate" v-if="row.Vendor">
              <i class="fas fa-store me-1"></i>{{ row.Vendor }}
            </div>
            
            <div class="small text-truncate text-muted" v-if="row.PartNumber">
              Part #: {{ row.PartNumber }}
            </div>
          </div>
        </div>
      </div>
      
      <!-- Empty state -->
      <div v-if="rows.length === 0" class="text-center text-muted py-5">
        <i class="fas fa-box-open fa-3x mb-3"></i>
        <p>No parts found</p>
      </div>
    </div>

    <!-- Filter Modal -->
    <kt-modal 
      :visible="filterVisible" 
      title="Filter Parts" 
      @dismiss="filterVisible = false"
    >
      <template #body>
        <part-filter
          :filter="filter" 
          @dismiss="filterVisible = false" 
          @update="setFilter($event)"
        ></part-filter>
      </template>
    </kt-modal>
  </div>
</template>

<style scoped>
.part-mobile {
  padding: 0.5rem;
}

.part-card {
  cursor: pointer;
  transition: transform 0.2s;
}

.part-card:active {
  transform: scale(0.98);
}

.part-card .card {
  border-left: 3px solid #0d6efd;
}

.mobile-part-list {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}
</style>

<script setup lang="ts">
import { ref } from "vue";
import KtModal from "@/components/UI/KtModal.vue";
import PartFilterRow from "@/components/Parts/PartFilterRow.vue";
import PartFilter from "@/components/Parts/PartFilter.vue";

// Props - receive data from parent
const props = defineProps<{
  rows: any[];
  total: number;
  filter: any;
}>();

// Emits
const emit = defineEmits<{
  refresh: [];
  newRecord: [];
  editRecord: [id: number];
  setFilter: [filter: any];
}>();

const filterVisible = ref(false);

// Methods
const refresh = () => {
  emit('refresh');
};

const newRecord = () => {
  emit('newRecord');
};

const editRecord = (id: number) => {
  emit('editRecord', id);
};

const setFilter = (filter: any) => {
  emit('setFilter', filter);
  filterVisible.value = false;
};

const getOrderStatusName = (statusId: number) => {
  const statusMap: Record<number, string> = {
    1: 'Pending',
    2: 'Ordered',
    3: 'Received',
    4: 'Installed',
    5: 'Cancelled'
  };
  return statusMap[statusId] || `Status #${statusId}`;
};

const getOrderStatusBadgeClass = (statusId: number) => {
  const classMap: Record<number, string> = {
    1: 'bg-warning',
    2: 'bg-info',
    3: 'bg-primary',
    4: 'bg-success',
    5: 'bg-danger'
  };
  return classMap[statusId] || 'bg-secondary';
};
</script>
